import React from 'react';
import { useNavigate } from 'react-router-dom';
import ChatWidget from '../components/ChatWidget';

const LAST_UPDATED = 'June 5, 2026';

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: '#AFA9EC', marginBottom: 12, letterSpacing: '-0.2px' }}>{title}</h2>
      <div style={{ fontSize: 14, color: 'rgba(240,239,233,0.7)', lineHeight: 1.8 }}>{children}</div>
    </div>
  );
}

function P({ children }) {
  return <p style={{ margin: '0 0 10px' }}>{children}</p>;
}

function Ul({ items }) {
  return (
    <ul style={{ margin: '8px 0 10px', paddingLeft: 20 }}>
      {items.map((item, i) => <li key={i} style={{ marginBottom: 6 }}>{item}</li>)}
    </ul>
  );
}

export default function Terms() {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100vh', background: '#0e0e0c', color: '#f0efe9', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Nav */}
      <div style={{ borderBottom: '0.5px solid rgba(255,255,255,0.07)', padding: '16px 32px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer' }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg,#7F77DD,#1D9E75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>F</span>
          </div>
          <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.8px', color: '#f0efe9' }}>FLO</span>
        </button>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '56px 24px 80px' }}>
        <div style={{ marginBottom: 48 }}>
          <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-1px', marginBottom: 10 }}>Terms of Service</h1>
          <p style={{ fontSize: 13, color: 'rgba(240,239,233,0.4)' }}>Last updated: {LAST_UPDATED}</p>
        </div>

        <Section title="Acceptance of Terms">
          <P>By creating an account or using FLO ("the Platform"), you agree to be bound by these Terms of Service. If you do not agree, do not use FLO.</P>
          <P>These terms apply to all users of the platform, including insurance agents and agencies who use FLO to manage leads and automate outreach.</P>
        </Section>

        <Section title="Platform Usage">
          <P>FLO provides an AI-powered CRM and SMS outreach platform designed for licensed insurance agents. You may use FLO only for lawful purposes in connection with your insurance business.</P>
          <P>You are responsible for:</P>
          <Ul items={[
            'Maintaining the security of your account credentials',
            'All activity that occurs under your account',
            'Ensuring your use of FLO complies with applicable federal and state laws',
            'The accuracy of any lead data you upload to the platform',
          ]} />
        </Section>

        <Section title="SMS Consent and Opt-Out">
          <P>FLO enables automated SMS outreach to leads on behalf of licensed agents. By using the SMS features, you represent and warrant that:</P>
          <Ul items={[
            'You have obtained any legally required consent to contact leads via SMS',
            'Your outreach complies with the Telephone Consumer Protection Act (TCPA) and all applicable state laws',
            'You will not use FLO to contact individuals who have opted out',
          ]} />
          <P>All leads can opt out of SMS at any time by replying <strong style={{ color: '#f0efe9' }}>STOP</strong>. The FLO platform automatically honors STOP requests and suppresses future messages to opted-out numbers. You must not attempt to override or bypass this opt-out mechanism.</P>
          <P>You are solely responsible for compliance with TCPA and any resulting claims, fines, or legal actions arising from your SMS campaigns.</P>
        </Section>

        <Section title="SMS Messaging Terms">
          <P>By providing your phone number to FLO or a FLO-affiliated insurance agent, you consent to receive SMS text messages related to insurance products, coverage options, appointment scheduling, and follow-up communications.</P>
          <P>Message frequency varies based on your interaction with our agents. Message and data rates may apply.</P>
          <P>To opt out at any time, reply <strong style={{ color: '#f0efe9' }}>STOP</strong> to any message. You will receive a confirmation message and no further texts will be sent.</P>
          <P>For help, reply <strong style={{ color: '#f0efe9' }}>HELP</strong> or contact us through the support chat on our website.</P>
          <P>We do not share your mobile information with third parties or affiliates for marketing or promotional purposes. Mobile opt-in data is not sold or shared.</P>
        </Section>

        <Section title="Subscription Plans & Billing">
          <P>FLO offers three subscription tiers billed monthly:</P>
          <div style={{ background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '16px 20px', margin: '12px 0' }}>
            {[
              { plan: 'Basic', price: '$97/mo', desc: 'AI outreach, CRM, appointments' },
              { plan: 'Pro', price: '$197/mo', desc: 'Includes automated lead scraping' },
              { plan: 'Pro+', price: '$497/mo', desc: '24/7 autonomous engine, unlimited leads' },
            ].map(({ plan, price, desc }) => (
              <div key={plan} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
                <div>
                  <span style={{ fontWeight: 600, color: '#f0efe9', fontSize: 14 }}>{plan}</span>
                  <span style={{ fontSize: 12, color: 'rgba(240,239,233,0.4)', marginLeft: 10 }}>{desc}</span>
                </div>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#AFA9EC' }}>{price}</span>
              </div>
            ))}
          </div>
          <P>All plans include a 14-day free trial. Billing begins automatically after the trial period unless you cancel. Subscriptions renew monthly unless cancelled before the renewal date.</P>
          <P>We reserve the right to change pricing with 30 days' notice to your registered email address.</P>
          <P>Refunds are not provided for partial billing periods or unused time remaining on a subscription.</P>
        </Section>

        <Section title="Credit Wallet">
          <P>FLO may offer a prepaid credit wallet for pay-as-you-go usage of certain platform features. Credits are non-refundable and have no cash value. Credits do not expire as long as your account remains active. Credits are forfeited if your account is terminated for violations of these Terms.</P>
        </Section>

        <Section title="Agent Responsibilities">
          <P>You represent that you are a licensed insurance agent or are operating under the supervision of a licensed agent, and that your use of FLO complies with your state's insurance licensing requirements.</P>
          <Ul items={[
            'You are responsible for the accuracy of any information communicated to leads through FLO',
            'You must review AI-generated content and ensure it complies with insurance regulations',
            'You must not make false or misleading statements about coverage, pricing, or carrier relationships',
            'You must maintain E&O (errors and omissions) insurance appropriate for your practice',
            'You are solely responsible for any appointments booked and follow-up with leads',
          ]} />
        </Section>

        <Section title="Agent Compliance Responsibility">
          <P>By using FLO's messaging features, you (the agent) warrant that you have obtained prior express consent from all recipients before sending any SMS messages through the platform. You agree to comply with all applicable laws including the Telephone Consumer Protection Act (TCPA) and CAN-SPAM Act.</P>
          <P>FLO provides messaging infrastructure only — you are solely responsible for ensuring all contacts have consented to receive messages, maintaining opt-out records, and complying with all federal, state, and carrier regulations. FLO is not liable for any claims, fines, or legal actions arising from your messaging campaigns.</P>
        </Section>

        <Section title="No Guarantee of Results">
          <P>FLO is a technology platform. We do not guarantee that use of FLO will result in any particular number of leads, appointments, sales, or revenue. Insurance sales results depend on many factors outside our control including market conditions, lead quality, agent skill, and regulatory environment.</P>
          <P>The platform is provided "as is" and "as available." We do not warrant that the platform will be uninterrupted, error-free, or free from viruses or other harmful components.</P>
        </Section>

        <Section title="Prohibited Uses">
          <P>You may not use FLO to:</P>
          <Ul items={[
            'Contact individuals who have not provided consent as required by applicable law',
            'Send spam, phishing messages, or deceptive communications',
            'Impersonate any person or entity',
            'Violate any applicable federal, state, or local law or regulation',
            'Attempt to reverse-engineer, scrape, or disrupt the platform',
            'Share your account credentials with unauthorized third parties',
          ]} />
        </Section>

        <Section title="Termination">
          <P>You may cancel your subscription at any time through your account settings. Your access will continue until the end of the current billing period.</P>
          <P>We reserve the right to suspend or terminate your account immediately if you violate these Terms, engage in fraudulent activity, or use the platform in a way that exposes FLO or its users to legal liability.</P>
        </Section>

        <Section title="Limitation of Liability">
          <P>To the maximum extent permitted by law, FLO shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the platform. Our total liability for any claim shall not exceed the amount you paid us in the 3 months preceding the claim.</P>
        </Section>

        <Section title="Changes to These Terms">
          <P>We may update these Terms from time to time. Material changes will be communicated via email to your registered address. Continued use of FLO after changes take effect constitutes your acceptance of the revised terms.</P>
        </Section>

        <Section title="Contact Us">
          <P>Questions about these Terms? Chat with FLO directly and get an instant reply.</P>
          <div style={{ marginTop: 16 }}>
            <ChatWidget />
          </div>
        </Section>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '0.5px solid rgba(255,255,255,0.07)', padding: '24px 32px', display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/privacy')} style={{ background: 'none', border: 'none', color: 'rgba(240,239,233,0.4)', cursor: 'pointer', fontSize: 12 }}>Privacy Policy</button>
        <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', color: 'rgba(240,239,233,0.4)', cursor: 'pointer', fontSize: 12 }}>Terms of Service</button>
        <button onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: 'rgba(240,239,233,0.4)', cursor: 'pointer', fontSize: 12 }}>Sign In</button>
      </div>
    </div>
  );
}
